import { useState, useMemo, useRef, useCallback, useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { iconsApi, type IconItem } from '../api/icons'
import { buildIconPath } from '@/lib/imagePaths'
import { composeIconOnFrame, toIconFilename } from '../lib/iconFraming'
import { cn } from '@/lib/utils'
import { EntityIcon } from '@/components/ui/entity-icon'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover'
import { Search, Upload, X, Check, Grid3X3, List, Pencil, Loader2, ClipboardPaste } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'

// Icon normalisation (framing onto vsiconblank.png) lives in lib/iconFraming so the
// icons ADMIN screen frames identically. Two copies would drift silently: icons added
// from here and icons added from there would simply stop matching, with nothing on
// screen to say which code path produced which.
//
// The paste flow below is the original; ImageIntake in components/shared was extracted
// from it for the admin screens and behaves the same way — document-level paste EVENT,
// no clipboard-read permission prompt.

interface IconPickerProps {
    value: string | null
    onChange: (value: string | null) => void
    disabled?: boolean
}

export function IconPicker({ value, onChange, disabled = false }: IconPickerProps) {
    const { t } = useTranslation()
    const { toast } = useToast()
    const queryClient = useQueryClient()
    const [open, setOpen] = useState(false)
    const [search, setSearch] = useState('')
    const fileInputRef = useRef<HTMLInputElement>(null)
    const [uploading, setUploading] = useState(false)
    const [composing, setComposing] = useState(false)
    const [viewMode, setViewMode] = useState<'icons' | 'list'>('icons')
    // Staged upload: after a file is chosen it's framed to the standard size, then we
    // collect a name + tags before creating the icon.
    const [pendingBlob, setPendingBlob] = useState<Blob | null>(null)
    const [pendingPreview, setPendingPreview] = useState<string | null>(null)
    const [pendingName, setPendingName] = useState('')
    const [pendingFallbackName, setPendingFallbackName] = useState('icon')
    const [pendingTags, setPendingTags] = useState('')
    const [pendingDescription, setPendingDescription] = useState('')
    // Paste target: the Paste button opens a zone that captures a Ctrl+V / drag image as a
    // paste EVENT (no clipboard-read permission prompt); clipboard-empty shows inline here.
    const [pasteMode, setPasteMode] = useState(false)
    const [pasteError, setPasteError] = useState<string | null>(null)

    const { data: iconsResponse } = useQuery({
        queryKey: ['icons'],
        queryFn: async () => {
            const res = await iconsApi.list()
            return res.data.data
        },
        staleTime: 5 * 60 * 1000,
    })

    const icons = iconsResponse ?? []

    const filtered = useMemo(() => {
        if (!search.trim()) return icons
        const term = search.toLowerCase()
        return icons.filter(
            (icon) =>
                icon.filename?.toLowerCase().includes(term) ||
                icon.description?.toLowerCase().includes(term) ||
                icon.tags?.toLowerCase().includes(term)
        )
    }, [icons, search])

    const selectedIcon = useMemo(
        () => (value ? icons.find((i) => String(i.id) === value) : null),
        [icons, value]
    )

    const handleSelect = useCallback(
        (icon: IconItem) => {
            onChange(String(icon.id))
            setOpen(false)
            setSearch('')
        },
        [onChange]
    )

    const resetPending = useCallback(() => {
        setPendingBlob(null)
        setPendingPreview(null)
        setPendingName('')
        setPendingFallbackName('icon')
        setPendingTags('')
        setPendingDescription('')
        setPasteMode(false)
        setPasteError(null)
        if (fileInputRef.current) fileInputRef.current.value = ''
    }, [])

    // Step 1: validate + frame the chosen image to the standard size; hold it until the
    // user confirms with a name/tags below.
    // Shared: validate + frame + stage an incoming image (from the file dialog OR the clipboard).
    const stageFile = useCallback(
        async (file: File, suggestedName?: string) => {
            const allowed = ['image/png', 'image/jpeg', 'image/gif', 'image/svg+xml', 'image/webp']
            if (!allowed.includes(file.type)) {
                toast({
                    title: 'Invalid image',
                    description: 'Please use a PNG, JPG, GIF, SVG, or WebP image.',
                    variant: 'destructive',
                })
                return
            }
            setComposing(true)
            try {
                const { blob, dataUrl } = await composeIconOnFrame(file)
                setPendingBlob(blob)
                setPendingPreview(dataUrl)
                setPendingName(suggestedName ?? file.name.replace(/\.[^.]+$/, ''))
                setPendingFallbackName(toIconFilename(file.name, 'icon').replace(/\.png$/, ''))
                setPendingTags('')
                setPendingDescription('')
            } catch {
                toast({
                    title: 'Could not process image',
                    description: 'The image could not be framed. Try a different one.',
                    variant: 'destructive',
                })
            } finally {
                setComposing(false)
            }
        },
        [toast]
    )

    const handleFileSelect = useCallback(
        async (e: React.ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0]
            if (file) await stageFile(file)
            if (fileInputRef.current) fileInputRef.current.value = ''
        },
        [stageFile]
    )

    // Paste flow: the button opens a paste zone (an OPTION); the image is captured there as a
    // paste EVENT — no clipboard-read permission prompt — and any problem shows inline.
    const openPaste = useCallback(() => { setPasteError(null); setPasteMode(true) }, [])
    const closePaste = useCallback(() => { setPasteError(null); setPasteMode(false) }, [])

    const acceptImageFile = useCallback(
        (file: File | null | undefined) => {
            if (!file) { setPasteError('No image found — copy an image, then paste.'); return }
            if (!file.type.startsWith('image/')) { setPasteError('That wasn’t an image. Copy an image and try again.'); return }
            setPasteError(null)
            stageFile(file, '')
        },
        [stageFile]
    )

    const handleZoneDrop = useCallback(
        (e: React.DragEvent) => { e.preventDefault(); acceptImageFile(e.dataTransfer?.files?.[0]) },
        [acceptImageFile]
    )

    // While the paste zone is open, capture Ctrl+V / Cmd+V at the document level so it works
    // regardless of focus — and without triggering the clipboard-read permission prompt.
    useEffect(() => {
        if (!pasteMode) return
        const onPaste = (e: ClipboardEvent) => {
            const item = Array.from(e.clipboardData?.items || []).find((it) => it.type.startsWith('image/'))
            if (item) { e.preventDefault(); acceptImageFile(item.getAsFile()) }
            else setPasteError('No image on your clipboard — copy an image (e.g. from PowerPoint) and paste again.')
        }
        document.addEventListener('paste', onPaste)
        return () => document.removeEventListener('paste', onPaste)
    }, [pasteMode, acceptImageFile])

    // Step 2: upload the framed PNG + create the DB record with name/tags/description.
    const handleConfirmUpload = useCallback(async () => {
        if (!pendingBlob) return
        const filename = toIconFilename(pendingName, pendingFallbackName)
        const uploadFile = new File([pendingBlob], filename, { type: 'image/png' })
        const tags = pendingTags.trim() || undefined
        const description = pendingDescription.trim() || undefined

        const finish = async (newIcon: IconItem) => {
            await queryClient.invalidateQueries({ queryKey: ['icons'] })
            onChange(String(newIcon.id))
            resetPending()
            setOpen(false)
            setSearch('')
            toast({ title: 'Icon added', description: `${newIcon.filename} added to library.` })
        }

        setUploading(true)
        try {
            await finish(await iconsApi.upload(uploadFile, description, tags))
        } catch (err: any) {
            const status = err?.response?.status ?? err?.status
            if (status === 409) {
                const ok = window.confirm(`An icon named "${filename}" already exists. Overwrite it?`)
                if (ok) {
                    try {
                        await finish(await iconsApi.upload(uploadFile, description, tags, true))
                    } catch (err2: any) {
                        toast({
                            title: 'Upload failed',
                            description: err2?.response?.data?.message || err2?.message || 'Failed to overwrite icon.',
                            variant: 'destructive',
                        })
                    }
                }
            } else {
                toast({
                    title: 'Upload failed',
                    description: err?.response?.data?.message || err?.message || 'Failed to upload icon.',
                    variant: 'destructive',
                })
            }
        } finally {
            setUploading(false)
        }
    }, [pendingBlob, pendingName, pendingFallbackName, pendingTags, pendingDescription, onChange, queryClient, toast, resetPending])

    const finalFilename = pendingBlob ? toIconFilename(pendingName, pendingFallbackName) : ''

    return (
        <Popover
            open={open}
            onOpenChange={(o) => { setOpen(o); if (!o) resetPending() }}
        >
            <div className="flex items-center gap-2">
                {selectedIcon ? (
                    <div className="flex items-center gap-2">
                        <EntityIcon
                            src={buildIconPath(selectedIcon.filename)}
                            alt=""
                            className="h-8 w-8 shrink-0"
                        />
                        <button
                            type="button"
                            className="text-muted-foreground hover:text-foreground"
                            onClick={(e) => { e.stopPropagation(); onChange(null) }}
                            disabled={disabled}
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </div>
                ) : (
                    <span className="text-muted-foreground text-sm">No icon</span>
                )}
                <PopoverTrigger asChild>
                    <Button
                        variant="outline"
                        size="sm"
                        className="h-8 px-2"
                        disabled={disabled}
                    >
                        <Pencil className="h-3.5 w-3.5" />
                    </Button>
                </PopoverTrigger>
            </div>
            <PopoverContent className="w-[520px] p-0" align="start">
                {pendingBlob ? (
                    /* Add-icon form: framed preview + name + tags + description */
                    <div className="p-4 space-y-3">
                        <div className="flex items-center gap-3">
                            {pendingPreview && (
                                <img
                                    src={pendingPreview}
                                    alt=""
                                    className="h-16 w-16 object-contain rounded-md border bg-background shrink-0"
                                />
                            )}
                            <div className="min-w-0">
                                <div className="text-sm font-medium">New icon</div>
                                <div className="text-xs text-muted-foreground">
                                    Resized &amp; centered in the standard icon frame.
                                </div>
                            </div>
                        </div>

                        <div>
                            <label className="text-xs font-medium">Name</label>
                            <Input
                                autoFocus
                                value={pendingName}
                                onChange={(e) => setPendingName(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter' && !uploading) handleConfirmUpload() }}
                                placeholder="e.g. vsiconpaloalto"
                                className="h-8 mt-1"
                            />
                            <p className="text-[11px] text-muted-foreground mt-1">
                                Saved as <span className="font-mono">{finalFilename}</span>
                            </p>
                        </div>

                        <div>
                            <label className="text-xs font-medium">Tags</label>
                            <Input
                                value={pendingTags}
                                onChange={(e) => setPendingTags(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter' && !uploading) handleConfirmUpload() }}
                                placeholder="e.g. Cisco, switch, network"
                                className="h-8 mt-1"
                            />
                            <p className="text-[11px] text-muted-foreground mt-1">
                                Comma-separated keywords (vendor, technology, category). Used to auto-select this
                                icon for matching objects &amp; services.
                            </p>
                        </div>

                        <div>
                            <label className="text-xs font-medium">
                                Description <span className="text-muted-foreground font-normal">(optional)</span>
                            </label>
                            <Input
                                value={pendingDescription}
                                onChange={(e) => setPendingDescription(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter' && !uploading) handleConfirmUpload() }}
                                placeholder="e.g. Palo Alto firewall"
                                className="h-8 mt-1"
                            />
                        </div>

                        <div className="flex items-center justify-end gap-2 pt-1">
                            <Button variant="ghost" size="sm" onClick={resetPending} disabled={uploading}>
                                Cancel
                            </Button>
                            <Button size="sm" onClick={handleConfirmUpload} disabled={uploading}>
                                {uploading ? 'Adding…' : 'Add to library'}
                            </Button>
                        </div>
                    </div>
                ) : pasteMode ? (
                    /* Paste zone — captures a Ctrl+V / drag image as a paste event (no prompt) */
                    <div className="p-4">
                        <div className="flex items-center justify-between mb-3">
                            <span className="text-sm font-medium">Paste an icon image</span>
                            <button
                                type="button"
                                onClick={closePaste}
                                className="text-xs text-muted-foreground hover:text-foreground"
                            >
                                Cancel
                            </button>
                        </div>
                        <div
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={handleZoneDrop}
                            className={cn(
                                'flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed py-12 px-4 text-center transition-colors',
                                pasteError ? 'border-destructive/50 bg-destructive/5' : 'border-muted-foreground/25'
                            )}
                        >
                            {composing ? (
                                <>
                                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                                    <span className="text-sm text-muted-foreground">Framing your icon…</span>
                                </>
                            ) : (
                                <>
                                    <ClipboardPaste className="h-6 w-6 text-muted-foreground" />
                                    <span className="text-sm">
                                        Press <kbd className="px-1.5 py-0.5 rounded border bg-muted text-xs font-mono">⌘V</kbd>
                                        {' / '}
                                        <kbd className="px-1.5 py-0.5 rounded border bg-muted text-xs font-mono">Ctrl+V</kbd> to paste
                                    </span>
                                    <span className="text-xs text-muted-foreground">or drag an image here</span>
                                    {pasteError && (
                                        <span className="text-xs text-destructive mt-1">{pasteError}</span>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="p-2 border-b">
                            <div className="flex items-center gap-2">
                                <Search className="h-4 w-4 text-muted-foreground shrink-0" />
                                <Input
                                    placeholder="Search icons..."
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    className="h-8 border-0 p-0 shadow-none focus-visible:ring-0"
                                />
                                <div className="flex shrink-0 border rounded-md">
                                    <button
                                        type="button"
                                        className={`p-1.5 rounded-l-md transition-colors ${viewMode === 'icons' ? 'bg-accent text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                                        onClick={() => setViewMode('icons')}
                                        title="Grid view"
                                    >
                                        <Grid3X3 className="h-3.5 w-3.5" />
                                    </button>
                                    <button
                                        type="button"
                                        className={`p-1.5 rounded-r-md transition-colors ${viewMode === 'list' ? 'bg-accent text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                                        onClick={() => setViewMode('list')}
                                        title="List view"
                                    >
                                        <List className="h-3.5 w-3.5" />
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div
                            style={{ height: '400px', overflowY: 'scroll' }}
                            className="p-1"
                            onWheel={(e) => e.stopPropagation()}
                            onTouchMove={(e) => e.stopPropagation()}
                        >
                            {filtered.length === 0 ? (
                                <div className="py-6 text-center text-sm text-muted-foreground">
                                    No icons found.
                                </div>
                            ) : viewMode === 'icons' ? (
                                <div className="grid grid-cols-5 gap-2">
                                    {filtered.map((icon) => {
                                        const isSelected = value === String(icon.id)
                                        return (
                                            <button
                                                key={icon.id}
                                                type="button"
                                                className={`relative flex flex-col items-center justify-center p-2 rounded-lg border hover:bg-accent cursor-pointer transition-colors ${
                                                    isSelected ? 'bg-accent ring-2 ring-primary border-primary' : 'border-transparent'
                                                }`}
                                                onClick={() => handleSelect(icon)}
                                                title={icon.description || icon.filename}
                                            >
                                                <EntityIcon
                                                    src={buildIconPath(icon.filename)}
                                                    alt={icon.filename}
                                                    className="h-14 w-14"
                                                />
                                                <span className="text-[10px] text-muted-foreground mt-1 truncate w-full text-center">
                                                    {(icon.description || icon.filename || '').replace(/^vsicon/, '').replace(/\.png$/, '')}
                                                </span>
                                                {isSelected && (
                                                    <div className="absolute top-0 right-0 bg-primary rounded-full p-0.5">
                                                        <Check className="h-2.5 w-2.5 text-primary-foreground" />
                                                    </div>
                                                )}
                                            </button>
                                        )
                                    })}
                                </div>
                            ) : (
                                <div className="flex flex-col gap-0.5">
                                    {filtered.map((icon) => {
                                        const isSelected = value === String(icon.id)
                                        return (
                                            <button
                                                key={icon.id}
                                                type="button"
                                                className={`flex items-center gap-3 px-2 py-1.5 rounded hover:bg-accent cursor-pointer transition-colors ${
                                                    isSelected ? 'bg-accent ring-2 ring-primary' : ''
                                                }`}
                                                onClick={() => handleSelect(icon)}
                                            >
                                                <EntityIcon
                                                    src={buildIconPath(icon.filename)}
                                                    alt={icon.filename}
                                                    className="h-8 w-8 shrink-0"
                                                />
                                                <span className="text-sm text-left truncate">
                                                    {icon.description || icon.filename}
                                                </span>
                                                {isSelected && (
                                                    <Check className="h-4 w-4 text-primary shrink-0 ml-auto" />
                                                )}
                                            </button>
                                        )
                                    })}
                                </div>
                            )}
                        </div>
                        <div className="border-t p-2 flex items-center justify-between">
                            <span className="text-xs text-muted-foreground">{filtered.length} icons</span>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/png,image/jpeg,image/gif,image/svg+xml,image/webp"
                                className="hidden"
                                onChange={handleFileSelect}
                            />
                            <div className="flex items-center gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={openPaste}
                                    disabled={composing}
                                    title="Paste an image (opens a paste box — then press Ctrl+V)"
                                >
                                    <ClipboardPaste className="h-4 w-4 mr-2" />
                                    Paste
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={composing}
                                >
                                    {composing ? (
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    ) : (
                                        <Upload className="h-4 w-4 mr-2" />
                                    )}
                                    {composing ? 'Processing…' : 'Upload'}
                                </Button>
                            </div>
                        </div>
                    </>
                )}
            </PopoverContent>
        </Popover>
    )
}
